import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

export const molndalPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-molndal",
    serviceCategory: "bortforsling",
    city: "göteborg",

    meta: {
      title: "Bortforsling Mölndal – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Mölndal. Vi hämtar möbler, vitvaror och skräp i Mölndal centrum, Krokslätt, Eklanda, Kållered och Lindome. Fast pris, ofta samma dag.",
      h1: "Bortforsling Mölndal",
    },

    hero: {
      heading: "Bortforsling i Mölndal",
      intro:
        "Boka bortforsling i Mölndal – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från lägenheterna i Mölndal centrum och Krokslätt till villorna i Eklanda, Kållered och Lindome. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Tusentals slutförda uppdrag i Göteborgsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Mölndal – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Mölndal",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Mölndal och körs till Återbruket Kikås",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Eklanda, Mölndal",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Mölndal centrum",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Krokslätt, Mölndal",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Kållered, Mölndal",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Lindome, Mölndal",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Mölndal – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Mölndal är en stad strax söder om Göteborg med runt 70 000 invånare och stora variationer i bebyggelsen – från det täta Mölndal centrum och Krokslätt vid Göteborgsgränsen till villaområdena i Eklanda, Toltorpsdalen och Bifrost, samt de lugnare orterna Kållered och Lindome längre söderut. Den blandningen gör bortforsling till olika utmaningar: i lägenheten handlar det ofta om trapphus och hiss, medan det i villan snarare handlar om ett fullt garage eller ett källarförråd. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Mölndal har en stor fördel här – staden driver två egna anläggningar, Återbruket Kikås och Lindome återvinningscentral, så det finns alltid en station nära din adress. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E6/E20 går rakt genom Mölndal och att Göteborgs spårvagnar och Kungsbackapendeln binder ihop staden – med stationer vid Mölndalsbro, Kållered och Lindome – tar vi oss snabbt mellan stadsdelarna. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både de täta kvarteren i Krokslätt och de smala gatorna kring historiska Kvarnbyn vid Mölndalsån – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Mölndal",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Eklanda, Kållered, Lindome eller Toltorpsdalen är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Mölndal centrum, Krokslätt eller Bifrost, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Mölndalsbo på Återbruket Kikås eller Lindome återvinningscentral.",
      additionalSections: [
        {
          heading: "Stadsdelar och områden vi täcker i Mölndal",
          content:
            "Vi utför bortforsling i hela Mölndals stad – från de täta kvarteren vid Göteborgsgränsen till villaområdena och de södra orterna. Oavsett om du bor i en lägenhet i Mölndal centrum, en villa i Eklanda, ett radhus i Kållered eller ett hus i Lindome kan vi vara på plats, ofta redan samma dag.\n\nI de centrala delarna – Mölndal centrum, Krokslätt, Kvarnbyn och Toltorpsdalen – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, nära Mölndalsbro och spårvagnen. Krokslätt vid Göteborgsgränsen är ett expanderande område med både äldre och nya flerbostadshus. I villaområdena Eklanda, Bifrost och Åby är vi vana vid garage, förråd och trädgårdar.\n\nVi är minst lika snabba i kommunens södra delar: Kållered med sitt handelsområde och Lindome med sin natur och småhusbebyggelse, båda längs E6 och Kungsbackapendeln. Tack vare närheten till E6/E20, Söderleden och pendeln planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon stadsdel.",
        },
        {
          heading: "Återvinning och miljöansvar i Mölndal",
          content:
            "En viktig del av vår bortforsling i Mölndal är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler. Mölndal har en stor fördel eftersom staden driver två egna anläggningar:\n\nÅterbruket Kikås – stadens största återvinningscentral, med stort fokus på återbruk, där sorterat grovavfall, elavfall, vitvaror och farligt avfall tas emot liksom föremål för återanvändning. Lindome återvinningscentral – smidig för uppdrag i de södra delarna kring Lindome och Kållered. Vi väljer alltid den station som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Stadsmissionen och Emmaus. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Mölndal med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Mölndals stadsdelar, från de täta kvarteren i Krokslätt och centrum till villatomterna i Eklanda och husen i Lindome, och vet att Återbruket Kikås och Lindome ÅVC ligger nära. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Göteborgsregionen och E6/E20 går rakt genom Mölndal. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon stadsdel i Mölndal, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Återbruket Kikås och Lindome återvinningscentral. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Mölndal?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon stadsdel i Mölndal.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Mölndal?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Mölndal?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och E6/E20 går rakt genom Mölndal, så vi tar oss snabbt mellan stadsdelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Mölndal?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Mölndal?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Mölndal centrum, Krokslätt och Bifrost. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och garage i Mölndal?",
        answer:
          "Ja, en stor del av våra uppdrag i Mölndal är villahämtningar i Eklanda, Toltorpsdalen, Kållered och Lindome. Vi backar in på uppfarten där det går och tömmer garage, källare och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Mölndal försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Mölndal?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. Mölndal driver två egna stationer – Återbruket Kikås och Lindome återvinningscentral – så det finns alltid en nära din adress. Det som kan återanvändas skänks till Stadsmissionen eller Emmaus.",
      },
      {
        question: "Kan jag boka bortforsling i Mölndal på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Mölndal?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både villaförråden i Eklanda och Lindome och lägenhetsförråden i Mölndal centrum och Krokslätt.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Mölndal?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet i Mölndal centrum och ditt förråd på en annan adress i kommunen.",
      },
      {
        question: "Vilka stadsdelar i Mölndal täcker ni?",
        answer:
          "Vi utför bortforsling i hela Mölndal – Mölndal centrum, Krokslätt, Kvarnbyn, Toltorpsdalen, Bifrost, Åby, Stensjön, Balltorp, Eklanda samt Kållered och Lindome. Inga extra avgifter för någon stadsdel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Mölndal?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Mölndalsbo på Återbruket Kikås eller Lindome återvinningscentral.",
      },
      {
        question: "Vilka kommuner runt Mölndal utför ni bortforsling i?",
        answer: `Vi finns i hela Göteborgsregionen, inklusive följande kommuner: ${goteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Mölndal i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Mölndal kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Mölndal idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Mölndals stad.",
    },

    en: {
      slug: "junk-removal-molndal",
      meta: {
        title: "Junk Removal Mölndal – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Mölndal. We pick up furniture, appliances and bulky items in Mölndal centrum, Krokslätt, Eklanda, Kållered and Lindome. Fixed price, often same day.",
        h1: "Junk Removal Mölndal",
      },
      hero: {
        heading: "Junk Removal in Mölndal",
        intro:
          "Book junk removal in Mölndal – we collect furniture, appliances and bulky items across the whole municipality, from the apartments in Mölndal centrum and Krokslätt to the villas in Eklanda, Kållered and Lindome. Quick booking, fixed price and often available the same day. Thousands of completed jobs in the Gothenburg region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Mölndal – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Mölndal",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Mölndal and driven to Återbruket Kikås",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Eklanda, Mölndal",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Mölndal centrum",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Krokslätt, Mölndal",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Kållered, Mölndal",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Lindome, Mölndal",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Mölndal – fast, hassle-free and responsible",
        mainText:
          "Mölndal is a city just south of Gothenburg with around 70,000 residents and great variety in its housing – from the dense Mölndal centrum and Krokslätt by the Gothenburg border to the villa areas of Eklanda, Toltorpsdalen and Bifrost, and the quieter towns of Kållered and Lindome further south. That mix makes junk removal a different challenge: in an apartment it is often stairwells and elevators, while in a villa it is more about a full garage or a basement storage room. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. Mölndal has a big advantage here – the city runs two of its own facilities, Återbruket Kikås and Lindome recycling centre, so there is always a station near your address. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E6/E20 motorway runs straight through Mölndal and Gothenburg's trams and the Kungsbacka commuter train link the city – with stations at Mölndalsbro, Kållered and Lindome – we move quickly between districts. This means we can often be on site the same day you book. Our workers are used to both the dense quarters of Krokslätt and the narrow streets around the historic Kvarnbyn mill district by the Mölndalsån river – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Mölndal",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Eklanda, Kållered, Lindome or Toltorpsdalen, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Mölndal centrum, Krokslätt or Bifrost, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Mölndal resident you can drop those off for free at Återbruket Kikås or Lindome recycling centre.",
        additionalSections: [
          {
            heading: "Districts and areas we cover in Mölndal",
            content:
              "We perform junk removal across the entire city of Mölndal – from the dense quarters by the Gothenburg border to the villa areas and the southern towns. Whether you live in an apartment in Mölndal centrum, a villa in Eklanda, a townhouse in Kållered or a house in Lindome, we can be on site, often the same day.\n\nIn the central parts – Mölndal centrum, Krokslätt, Kvarnbyn and Toltorpsdalen – we are highly experienced with apartment pickups via elevator and stairwell, close to Mölndalsbro and the tram. Krokslätt by the Gothenburg border is an expanding area with both older and new apartment buildings. In the villa areas of Eklanda, Bifrost and Åby we are used to garages, storage rooms and gardens.\n\nWe are just as quick in the southern parts of the municipality: Kållered with its retail area and Lindome with its nature and detached housing, both along the E6 and the Kungsbacka commuter train. Thanks to the proximity of the E6/E20, Söderleden and the commuter train, we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Mölndal",
            content:
              "An important part of our junk removal in Mölndal is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. Mölndal has a big advantage because the city runs two of its own facilities:\n\nÅterbruket Kikås – the city's largest recycling centre, with a strong focus on reuse, which receives sorted bulky waste, e-waste, white goods and hazardous waste as well as items for reuse. Lindome recycling centre – convenient for jobs in the southern parts around Lindome and Kållered. We always choose the station nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Stadsmissionen and Emmaus. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Mölndal with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Mölndal's districts, from the dense quarters of Krokslätt and the centre to the villa plots of Eklanda and the houses in Lindome, and that Återbruket Kikås and Lindome recycling centre are close by. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Gothenburg region and the E6/E20 runs straight through Mölndal. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any district in Mölndal, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via Återbruket Kikås and Lindome recycling centre. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Mölndal cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district in Mölndal.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Mölndal?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Mölndal?",
          answer:
            "We have vans and staff across the Gothenburg region and the E6/E20 runs straight through Mölndal, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Mölndal?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Mölndal?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Mölndal centrum, Krokslätt and Bifrost. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and garages in Mölndal?",
          answer:
            "Yes, a large share of our jobs in Mölndal are villa pickups in Eklanda, Toltorpsdalen, Kållered and Lindome. We reverse onto the driveway where possible and clear garages, basements and storage rooms. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Mölndal insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Mölndal?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Mölndal runs two of its own stations – Återbruket Kikås and Lindome recycling centre – so there is always one near your address. Items that can be reused are donated to Stadsmissionen or Emmaus.",
        },
        {
          question: "Can I book junk removal in Mölndal on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Mölndal?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage rooms, attics and garages. We are used to both the villa storage rooms in Eklanda and Lindome and the apartment storage units in Mölndal centrum and Krokslätt.",
        },
        {
          question: "Can you make multiple stops during junk removal in Mölndal?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment in Mölndal centrum and a storage unit at another address in the municipality, for example.",
        },
        {
          question: "Which districts in Mölndal do you cover?",
          answer:
            "We perform junk removal across all of Mölndal – Mölndal centrum, Krokslätt, Kvarnbyn, Toltorpsdalen, Bifrost, Åby, Stensjön, Balltorp, Eklanda, and Kållered and Lindome. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Mölndal?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Mölndal resident you can drop those off for free at Återbruket Kikås or Lindome recycling centre.",
        },
        {
          question: "Which municipalities around Mölndal do you serve?",
          answer:
            "We operate across the entire Gothenburg region, including Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Mölndal in 3 easy steps",
        step2Text:
          "Our young workers in Mölndal come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Mölndal today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole city of Mölndal.",
      },
    },
  },
];
